import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Box, Button, Flex, HStack, IconButton, Image, Link, Spinner, Switch, Text, Textarea, VStack,
  useBreakpointValue, usePrefersReducedMotion,
} from "@chakra-ui/react";
import ActionTooltip from "../ActionTooltip";
import { useUser } from "../../contexts/UserContext";
import { tt } from "../../i18n/tt";
import { askLay, escalateLay, escalationState, scopeFromRole, describeElement, LAY_MAX_MESSAGE, LAY_ASK_EVENT } from "../../lib/layChat";
import { readLayPrefs, writeLayPrefs } from "../../lib/layPrefs";
import { announceLayOpen, onOpsChatOpenChange } from "../../lib/floatingStack";
import { isImpersonating } from "../../lib/policyConsentRules";
import screenDictionary from "../../lib/layScreenDictionary.json";

/**
 * 앱 안 레이(Lay) 위젯 — 우측 하단 런처 + 패널. 2026-09-09 대표 결정: 시안 A(떠 있는 패널)·B(화면 옆에 붙이기)·C(화면 도우미)를
 * 모두 넣고 사용자가 「레이 설정」 시트(통합 시안 2 채택)에서 고른다. 기획 docs/ops/lay-ai-operations-plan.md §3-1·§3-5,
 * 시안 docs/design/lay-ai-operations-combined-variants.html. 문서: docs/pages/main/LayWidget.md
 *
 * - 답은 가이드 도우미 함수 `lay-docs-chat`(v9) 이 가이드 원문만 근거로 만든다. 역할 판정은 서버. 이 위젯은 질문·화면 맥락만 보낸다.
 * - 「화면에서 고르기」: 화면의 요소를 클릭하면 그 요소의 문구·위치를 맥락으로 붙여 묻는다(캡처 없음 — 문구만).
 *   정산·계좌·연락처 화면(`/total`·`/partner-settlement`·`/edit-profile`·`/change-password`·`/withdrawal`)에서는 도구를 숨긴다.
 * - 대화는 세션(sessionStorage)에만 남고, 영구 기록은 서버(lay_conversations)가 한다 → 「내 대화 기록」(/settings/lay/history).
 * - 「운영팀에 물어볼까요?」(v11, 기획 §3-5 (나)): 가이드에 없는 답이면 서버가 바로 넘기지 않고 `escalation: "offer"` 로 돌려준다.
 *   여기서 예/아니요를 묻고, 「예」일 때만 escalateLay() 로 넣는다. 「아니요」는 서버에 보내지 않고 가이드·문의 안내로 끝낸다.
 * - 카카오톡 레이는 보류(대표) — 여기 없다. AI·베타 표기는 가이드 위젯과 같다(인공지능기본법 고지).
 */

const STORE_KEY = "laeebly-lay-widget:v1";
const RATE_LOCK_SECONDS = 60;
const NO_PICK_PATHS = ["/total", "/partner-settlement", "/partner/results", "/edit-profile", "/change-password", "/withdrawal", "/total-copyrights"];
const GUIDE_URL = "https://docs.laeebly.io";

const SUGGESTIONS = {
  creator: ["채널 등록은 어떻게 하나요?", "정산은 언제 신청할 수 있나요?", "식별코드가 뭔가요?", "등급은 언제 정해지나요?"],
  rights: ["검수 반려 사유는 어떻게 고르나요?", "정산내역서는 어디서 보나요?", "협업 채널은 어떻게 고르나요?", "작품 제공 조건은 어떻게 정하나요?"],
};
// 파트너 범위(2026-09-10) — 파트너 문서(p-*) + 크리에이터·권리사 가이드가 근거(대표 결정). 파트너 문서 출처는 앱 안 /partner/docs/<slug>,
// 그 밖은 가이드 사이트. 「문의하기」(/support)는 파트너 허용 경로 밖이라 숨기고, 「내 대화 기록」은 /partner/lay/history.
SUGGESTIONS.partner = ["파트너 코드가 붙은 링크는 어떻게 만드나요?", "파트너가 겹치면 수익은 어떻게 나뉘나요?", "홍보하면 안 되는 곳은 어디인가요?", "재수료는 언제 해야 하나요?"];
SUGGESTIONS.all = SUGGESTIONS.creator;
const PARTNER_DOCS_PATH = "/partner/docs";
const PARTNER_HISTORY_PATH = "/partner/lay/history";
const isPartnerDocSlug = (slug) => /^p-/.test(String(slug || ""));

function readStore() { try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || "null"); } catch (e) { return null; } }
function writeStore(v) { try { sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* noop */ } }

const mdComponents = {
  p: (p) => <Text fontSize="13.5px" lineHeight="1.65" mb={1.5} sx={{ "&:last-child": { mb: 0 } }} {...p} />,
  ol: (p) => <Box as="ol" pl={5} mb={1.5} fontSize="13.5px" lineHeight="1.65" {...p} />,
  ul: (p) => <Box as="ul" pl={5} mb={1.5} fontSize="13.5px" lineHeight="1.65" {...p} />,
  li: (p) => <Box as="li" mb={0.5} {...p} />,
  strong: (p) => <Text as="strong" fontWeight="600" {...p} />,
  a: (p) => <Link color="brand.700" textDecoration="underline" textUnderlineOffset="2px" isExternal {...p} />,
  code: (p) => <Text as="code" fontSize="0.9em" bg="blackAlpha.100" px={1} borderRadius="3px" {...p} />,
};

function SparkIcon({ size = 14 }) {
  return (
    <Box as="svg" viewBox="0 0 24 24" w={`${size}px`} h={`${size}px`} flexShrink={0} aria-hidden="true">
      <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9L12 2z" fill="#3cb7a0" />
      <path d="M19 15l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9L19 15z" fill="#7c6cf2" />
    </Box>
  );
}
function AiTag({ dark }) {
  return (
    <Text as="span" fontFamily="mono" fontSize="10px" fontWeight="600" letterSpacing="0.08em" lineHeight="1.3" px={1.5} borderRadius="4px"
      color={dark ? "brand.400" : "brand.700"} borderWidth="1px" borderColor="rgba(60,183,160,0.55)" flexShrink={0} aria-label={tt("a.cfecd21f", "AI 도우미")}>AI</Text>
  );
}
function BetaRibbon({ top = "7px", right = "-26px" }) {
  return (
    <Box as="span" position="absolute" top={top} right={right} transform="rotate(38deg)" bg="accent.400" color="neutral.900" fontFamily="mono"
      fontSize="8.5px" fontWeight="700" letterSpacing="0.12em" lineHeight="1.3" px="30px" py="2px" pointerEvents="none" whiteSpace="nowrap" aria-label={tt("a.705f1053", "베타 서비스")}>BETA</Box>
  );
}
function Logo({ size }) { return <Image src={`${process.env.PUBLIC_URL}/laeebly-icon.png`} alt="" w={`${size}px`} h={`${size}px`} draggable={false} />; }

function Sources({ sources, scope }) {
  if (!sources?.length) return null;
  const partner = scope === "partner";
  return (
    <Flex mt={2} direction="column" align="flex-start" gap={0}>
      <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.08em" color="neutral.500" mb={0.5}>{tt("a.20e87f54", "출처")}</Text>
      {sources.map((s, i) => (
        s.kind === "kb" ? (
          <Text key={`${s.slug}-${i}`} fontSize="11.5px" fontWeight="500" color="accent.800" py={0.5}>{tt("a.7cd1439a", "운영팀 답변")} · {s.title}{s.version ? ` (v${s.version})` : ""}</Text>
        ) : (
          <Link key={`${s.slug}-${i}`} {...(partner && isPartnerDocSlug(s.slug) ? { as: RouterLink, to: `${PARTNER_DOCS_PATH}/${s.slug}` } : { href: `${GUIDE_URL}/${s.slug}`, isExternal: true })} fontSize="11.5px" fontWeight="500" color="brand.700" textDecoration="underline" textUnderlineOffset="3px" py={0.5}>
            {s.title || s.slug}{s.heading ? ` › ${s.heading}` : ""}
          </Link>
        )
      ))}
    </Flex>
  );
}

// 「운영팀에 물어볼까요?」 — offer(되묻기) → sent(전달됨) | declined(가이드·문의 안내). 기획 §3-5 (나).
function Escalation({ m, onDecide, scope }) {
  const state = escalationState(m);
  if (state === "none") return null;
  const partner = scope === "partner";
  const guideBtn = partner
    ? <Button as={RouterLink} to={PARTNER_DOCS_PATH} size="xs" variant="outline" borderColor="neutral.300">{tt("a.45831d88", "파트너 가이드")}</Button>
    : <Button as="a" href={GUIDE_URL} target="_blank" rel="noopener noreferrer" size="xs" variant="outline" borderColor="neutral.300">{tt("a.a95989ae", "이용 가이드")}</Button>;
  const box = { mt: 2, px: 3, py: 2.5, fontSize: "12.5px", lineHeight: "1.6", bg: "neutral.50", border: "1px solid", borderColor: "neutral.200", borderRadius: "4px", color: "neutral.700" };
  if (state === "offer") {
    return (
      <Box {...box} role="group" aria-label={tt("a.ae469d76", "이건 제가 확실히 몰라요. 운영팀에 물어볼까요?")}>
        <Text>{tt("a.ae469d76", "이건 제가 확실히 몰라요. 운영팀에 물어볼까요?")}</Text>
        {m.escalating ? (
          <HStack spacing={2} mt={2} color="neutral.500"><Spinner size="xs" color="brand.500" /><Text>{tt("a.3470e1c2", "전달 중…")}</Text></HStack>
        ) : (
          <HStack spacing={1.5} mt={2}>
            <Button size="xs" bg="neutral.900" color="white" _hover={{ bg: "neutral.800" }} onClick={() => onDecide("yes")}>{tt("a.bd040d31", "네, 물어봐 주세요")}</Button>
            <Button size="xs" variant="outline" borderColor="neutral.300" onClick={() => onDecide("no")}>{tt("a.6ab4fd62", "아니요, 괜찮아요")}</Button>
          </HStack>
        )}
        {m.escalateError && <Text mt={1.5} color="status.error" role="alert">{m.escalateError}</Text>}
      </Box>
    );
  }
  if (state === "declined") {
    return (
      <Box {...box}>
        <Text>{partner ? tt("a.6576a1ec", "알겠어요. 파트너 가이드에서 더 찾아보시거나, 담당자에게 물어보셔도 돼요.") : tt("a.131e5bfc", "알겠어요. 가이드에서 더 찾아보시거나, 나중에 「문의하기」로 물어보셔도 돼요.")}</Text>
        <HStack spacing={1.5} mt={2}>
          {guideBtn}
          {!partner && <Button as="a" href="/support" size="xs" variant="outline" borderColor="neutral.300">{tt("a.4f1a014e", "문의하기")}</Button>}
        </HStack>
      </Box>
    );
  }
  return (
    <Box {...box}>
      <Text>{tt("a.157e026b", "가이드에 없는 내용이라 운영팀에 전달됐어요. 답이 오면 「내 대화 기록」에 담당자 확인 답변으로 붙고, 앱 알림으로 알려 드려요.")}</Text>
      <HStack spacing={1.5} mt={2}>
        <Button as={RouterLink} to={partner ? PARTNER_HISTORY_PATH : "/settings/lay/history"} size="xs" variant="outline" borderColor="neutral.300">{tt("a.38331098", "내 대화 기록")}</Button>
        {!partner && <Button as="a" href="/support" size="xs" variant="outline" borderColor="neutral.300">{tt("a.4f1a014e", "문의하기")}</Button>}
      </HStack>
    </Box>
  );
}

function Message({ m, onDecide, scope }) {
  const isUser = m.role === "user";
  return (
    <Box>
      <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.08em" color={isUser ? "neutral.400" : "brand.600"} mb={1}>{isUser ? tt("a.4b709453", "나") : tt("a.54c22d67", "레이")}</Text>
      {isUser && m.context && (
        <Text fontSize="11px" color="brand.700" bg="brand.50" border="1px solid" borderColor="rgba(60,183,160,0.4)" borderRadius="6px" px={2} py={1} mb={1.5} display="inline-block" noOfLines={2}>⌖ {m.context}</Text>
      )}
      <Box color="neutral.800" fontSize="13.5px" lineHeight="1.65" {...(isUser ? {} : { borderLeftWidth: "2px", borderColor: "brand.500", pl: 3 })}>
        {isUser ? <Text whiteSpace="pre-wrap">{m.text}</Text> : <ReactMarkdown components={mdComponents}>{m.text}</ReactMarkdown>}
        {!isUser && <Sources sources={m.sources} scope={scope} />}
        {!isUser && <Escalation m={m} onDecide={onDecide} scope={scope} />}
      </Box>
    </Box>
  );
}

// ── 레이 설정 시트 (통합 시안 2) ────────────────────────────────────────────
function SettingsSheet({ prefs, onChange, onClose, onHistory, onHelp }) {
  const Opt = ({ v, title, desc, pic }) => (
    <Button variant="unstyled" h="auto" textAlign="left" p={2.5} border="1px solid" borderColor={prefs.view === v ? "brand.500" : "neutral.300"} bg={prefs.view === v ? "brand.50" : "white"} borderRadius="8px" onClick={() => onChange({ view: v })} display="flex" flexDirection="column" alignItems="stretch" gap={1.5} whiteSpace="normal">
      <Box h="40px" borderRadius="5px" bg="neutral.100" border="1px solid" borderColor="neutral.200" position="relative" overflow="hidden">
        {pic === "float" ? <Box position="absolute" right="5px" bottom="5px" w="18px" h="22px" bg="neutral.900" borderRadius="2px" /> : <Box position="absolute" right={0} top={0} bottom={0} w="30%" bg="neutral.900" />}
      </Box>
      <Text fontSize="13px" fontWeight="600" color="neutral.800">{title}</Text>
      <Text fontSize="11.5px" color="neutral.500" lineHeight="1.4">{desc}</Text>
    </Button>
  );
  const Row = ({ title, desc, k, disabled }) => (
    <Flex align="center" gap={3} py={1.5}>
      <Box flex="1"><Text fontSize="13px" color="neutral.800">{title}</Text><Text fontSize="11.5px" color="neutral.500">{desc}</Text></Box>
      <Switch size="sm" colorScheme="teal" isChecked={!!prefs[k]} isDisabled={disabled} onChange={(e) => onChange({ [k]: e.target.checked })} />
    </Flex>
  );
  return (
    <Box position="absolute" inset={0} bg="white" zIndex={2} display="flex" flexDirection="column" overflowY="auto">
      <Flex align="center" px={3.5} py={3} borderBottom="1px solid" borderColor="neutral.200">
        <Text fontWeight="700" fontSize="14px" color="neutral.900">{tt("a.30e284e8", "레이 설정")}</Text>
        <IconButton ml="auto" aria-label={tt("a.218e2ae6", "닫기")} size="sm" variant="ghost" onClick={onClose} icon={<Text fontSize="18px" lineHeight="1">×</Text>} />
      </Flex>
      <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="neutral.200" />}>
        <Box px={3.5} py={3}>
          <Text fontSize="11px" fontWeight="600" letterSpacing="0.06em" color="neutral.500" textTransform="uppercase" mb={2}>{tt("a.31198462", "보기 방식")}</Text>
          <Flex gap={2}>
            <Opt v="float" pic="float" title={tt("a.29cb8bb1", "떠 있는 패널")} desc={tt("a.ffd7fc20", "우측 하단에 작게. 화면을 덜 가려요")} />
            <Opt v="side" pic="side" title={tt("a.4014e9db", "화면 옆에 붙이기")} desc={tt("a.af21b0cd", "화면과 나란히. 보면서 물어요")} />
          </Flex>
          <Text mt={1.5} fontSize="11px" color="neutral.400">{tt("a.6a443dc1", "휴대폰에서는 항상 전체 화면으로 열려요")}</Text>
        </Box>
        <Box px={3.5} py={2}>
          <Row k="me" title={tt("a.lay.me.title", "내 상황 보고 답하기")} desc={tt("a.lay.me.desc", "내 채널·검수·정산 단계와 금액, 계좌 뒤 4자리를 읽어 지금 상황에 맞춰 답해요. 금액은 앱 정산 화면이 기준이고, 읽은 내용은 대화 기록에 남기지 않아요")} />
          <Row k="assist" title={tt("a.47b7b581", "화면 도우미 ⌖")} desc={tt("a.b1254097", "「화면에서 고르기」로 궁금한 곳을 찍어 물을 수 있어요")} />
          <Row k="tts" title={tt("a.0508dd7f", "답을 소리로 읽어 주기")} desc={tt("a.f9db101e", "준비 중 — 음성 기능과 함께 열려요")} disabled />
        </Box>
        <Box px={3.5} py={3}>
          <Text fontSize="11px" fontWeight="600" letterSpacing="0.06em" color="neutral.500" textTransform="uppercase" mb={1.5}>{tt("a.cd889ef4", "기록")}</Text>
          <Flex align="center" gap={3}>
            <Box flex="1"><Text fontSize="13px" color="neutral.800">{tt("a.38331098", "내 대화 기록")}</Text><Text fontSize="11.5px" color="neutral.500">{tt("a.b4aa155b", "레이와 나눈 모든 대화 · 운영팀 답변 포함")}</Text></Box>
            <Button size="xs" variant="link" color="brand.700" fontWeight="600" onClick={onHistory}>{tt("a.4c96914d", "보기 →")}</Button>
          </Flex>
          {/* 가이드 「레이(AI 도우미)에게 물어보기」 — 크리에이터·권리사는 docs 사이트 cr-lay, 파트너는 허브 안 p-lay (2026-09-10) */}
          <Flex align="center" gap={3} mt={2}>
            <Box flex="1"><Text fontSize="13px" color="neutral.800">{tt("a.af007ad9", "도움말")}</Text><Text fontSize="11.5px" color="neutral.500">{tt("a.56d37a77", "레이 사용법 · 무엇을 물을 수 있고 모를 때는 어떻게 되는지")}</Text></Box>
            <Button size="xs" variant="link" color="brand.700" fontWeight="600" onClick={onHelp}>{tt("a.e518bd53", "열기 →")}</Button>
          </Flex>
        </Box>
        <Box px={3.5} py={3} fontSize="11.5px" color="neutral.500" lineHeight="1.6">
          {tt("a.b3ca820e", "레이는 AI 도우미예요. 정산·계약 확정 안내는 운영팀 답변이 기준이에요. 설정은 이 브라우저에 저장돼요.")}
        </Box>
      </VStack>
    </Box>
  );
}

// ── 본체 ─────────────────────────────────────────────────────────────────
export default function LayWidget() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const reduceMotion = usePrefersReducedMotion();
  const scope = scopeFromRole(userInfo?.role);

  const [prefs, setPrefs] = useState(() => readLayPrefs());
  useEffect(() => { const h = (e) => setPrefs(e.detail || readLayPrefs()); window.addEventListener("laeebly:lay-prefs", h); return () => window.removeEventListener("laeebly:lay-prefs", h); }, []);
  const changePrefs = (patch) => setPrefs(writeLayPrefs(patch));
  const side = !isMobile && prefs.view === "side";

  const [open, setOpen] = useState(false);
  // 운영팀 대화방 버튼(바로 위 자리)과 겹치지 않게 — 레이 창이 열리면 알리고, 운영팀 대화창이 열려 있으면 런처를 숨긴다(src/lib/floatingStack.js · 2026-10-23)
  const [opsChatOpen, setOpsChatOpen] = useState(false);
  useEffect(() => { announceLayOpen(open); }, [open]);
  useEffect(() => () => announceLayOpen(false), []);
  useEffect(() => onOpsChatOpenChange(setOpsChatOpen), []);
  const [settings, setSettings] = useState(false);
  // 되묻기 전달 중(escalating) 표시는 진행 상태라 저장본에서는 뺀다 — 새로 고침 뒤 스피너가 영원히 돌지 않게.
  const [messages, setMessages] = useState(() => { const s = readStore(); return s && s.scope === scope ? (s.messages || []).map(({ escalating, ...m }) => m) : []; });
  const [conversationId, setConversationId] = useState(() => { const s = readStore(); return s && s.scope === scope ? s.conversationId || null : null; });
  const [input, setInput] = useState("");
  const [hp, setHp] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState(null); // { label, context }
  const launcherRef = useRef(null); const inputRef = useRef(null); const listRef = useRef(null); const scopeRef = useRef(scope);

  useEffect(() => { if (scopeRef.current !== scope) { scopeRef.current = scope; setMessages([]); setConversationId(null); setError(null); } }, [scope]);
  useEffect(() => { writeStore({ scope, messages, conversationId }); }, [scope, messages, conversationId]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e) => { if (e.key === "Escape") { if (picking) setPicking(false); else setOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open, picking]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, sending, open]);
  // 상시 도움말(GuideHint) 「레이에게 묻기」 — 위젯을 열고 질문을 입력칸에 미리 채운다. 보내는 건 사용자가 누른다.
  useEffect(() => {
    const h = (e) => {
      const q = String(e?.detail?.question || "").trim();
      if (!q) return;
      setSettings(false); setPicking(false); setOpen(true);
      setInput(q.slice(0, LAY_MAX_MESSAGE));
      setTimeout(() => inputRef.current?.focus(), 80);
    };
    window.addEventListener(LAY_ASK_EVENT, h);
    return () => window.removeEventListener(LAY_ASK_EVENT, h);
  }, []);
  useEffect(() => { if (lockedUntil <= Date.now()) return undefined; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [lockedUntil]);
  const lockedSeconds = Math.max(0, Math.ceil((lockedUntil - now) / 1000));

  // 「화면에서 고르기」 — 위젯 밖 요소를 한 번 클릭하면 문구·위치를 맥락으로 담는다. 캡처는 하지 않는다.
  const pickAllowed = prefs.assist && !NO_PICK_PATHS.some((p) => location.pathname.startsWith(p));
  useEffect(() => {
    if (!picking) return undefined;
    document.body.style.cursor = "crosshair";
    const onClick = (e) => {
      const el = e.target;
      if (el.closest?.("#lay-widget-panel, #lay-widget-launcher")) return;
      e.preventDefault(); e.stopPropagation();
      const ctx = describeElement(el, location.pathname);
      const label = (el.getAttribute?.("data-lay") || el.getAttribute?.("aria-label") || (el.innerText || "").trim().replace(/\s+/g, " ").slice(0, 40) || el.tagName.toLowerCase());
      setPicked({ label, context: ctx });
      setPicking(false);
      setInput((v) => v || tt("a.06ef328f", "이건 뭐예요?"));
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    document.addEventListener("click", onClick, true);
    return () => { document.body.style.cursor = ""; document.removeEventListener("click", onClick, true); };
  }, [picking, location.pathname]);

  const send = useCallback(async (raw) => {
    const message = String(raw || "").trim().slice(0, LAY_MAX_MESSAGE);
    if (!message || sending || lockedSeconds > 0) return;
    setError(null); setInput("");
    // 화면 사전(docs/pages → scripts/lay/build-screen-dictionary.js): 지금 보는 화면이 무엇인지 한 문단을 맥락에 붙인다
    const screen = (screenDictionary.entries || []).find((e) => e.routes.some((r) => r !== "/" && location.pathname.startsWith(r))) || (screenDictionary.entries || []).find((e) => e.routes.includes(location.pathname));
    const screenNote = screen ? " " + tt("a.05bf8e65", "· 화면 「{{v0}}」: {{v1}}", { v0: screen.title, v1: screen.role.slice(0, 280) }) : "";
    const context = (picked ? picked.context : `경로 ${location.pathname}`) + screenNote;
    setMessages((m) => [...m, { role: "user", text: message, context: picked ? picked.label : null }]);
    setPicked(null); setSending(true);
    try {
      // me(2026-09-13 맞춤 답변): 설정이 켜져 있고 대리 접속이 아닐 때만 서버가 내 상황을 읽는다. 대리 접속 중에는 회원 상황을 프롬프트·기록에 싣지 않는다.
      const r = await askLay({ message, conversationId, context, honeypot: hp, me: prefs.me !== false && !isImpersonating() });
      if (r.conversationId) setConversationId(r.conversationId);
      // 메시지에 대화 id·메시지 id 를 같이 둔다 — 「새 대화」로 conversationId 가 바뀐 뒤에도 이 답의 되묻기가 제 대화에 붙게.
      setMessages((m) => [...m, { role: "lay", text: r.content, sources: r.sources, escalated: r.escalated, escalation: r.escalation, conversationId: r.conversationId, messageId: r.messageId }]);
    } catch (e) {
      if (e?.status === 429) setLockedUntil(Date.now() + (e.retryAfter || RATE_LOCK_SECONDS) * 1000);
      setError(e?.content || tt("a.6567a373", "지금 답변 생성이 어려워요. 잠시 후 다시 시도해 주세요."));
    } finally { setSending(false); }
  }, [sending, lockedSeconds, picked, location.pathname, conversationId, hp, prefs.me]);

  // 「운영팀에 물어볼까요?」 예/아니요. 「아니요」는 서버에 보내지 않는다(기획 §3-5 (나) — 슬랙 미발송, 가이드·문의 안내로 종료).
  const decide = useCallback(async (idx, decision) => {
    const patch = (p) => setMessages((ms) => ms.map((x, i) => (i === idx ? { ...x, ...p } : x)));
    const m = messages[idx];
    if (!m || escalationState(m) !== "offer" || m.escalating) return;
    if (decision === "no") { patch({ escalation: "declined", escalateError: null }); return; }
    patch({ escalating: true, escalateError: null });
    try {
      await escalateLay({ conversationId: m.conversationId || conversationId, messageId: m.messageId });
      patch({ escalation: "sent", escalating: false });
    } catch (e) {
      patch({ escalating: false, escalateError: e?.content || tt("a.8b424c34", "전달에 실패했어요. 다시 시도해 주세요.") });
    }
  }, [messages, conversationId]);

  const reset = () => { setMessages([]); setConversationId(null); setError(null); setInput(""); setPicked(null); inputRef.current?.focus(); };
  const close = useCallback(() => { setOpen(false); setSettings(false); setPicking(false); setTimeout(() => launcherRef.current?.focus(), 0); }, []);
  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(input); } };
  const transition = reduceMotion ? undefined : "opacity 160ms ease, transform 160ms ease";

  // 화면 옆에 붙이기: 본문이 가려지지 않도록 body 오른�emphasis 여백을 준다(패널 폭만큼).
  useEffect(() => {
    if (!(open && side)) return undefined;
    const prev = document.body.style.paddingRight; document.body.style.paddingRight = "380px";
    return () => { document.body.style.paddingRight = prev; };
  }, [open, side]);

  return (
    <>
      {!(open && (isMobile || side)) && !opsChatOpen && (
        <Box id="lay-widget-launcher" position="fixed" right={{ base: "16px", md: "20px" }} bottom={{ base: "76px", md: "20px" }} zIndex={1200}>
          <Box position="relative" display="inline-block" overflow="hidden" borderRadius="8px" boxShadow="lg" transition={transition}>
            <Button ref={launcherRef} aria-label={tt("a.b05d2464", "레이에게 묻기 (AI 도우미 · 베타)")} aria-expanded={open} aria-controls="lay-widget-panel"
              onClick={() => setOpen((o) => !o)} h="44px" minW="44px" pl={3} pr={{ base: 3, md: "34px" }} borderRadius="8px" bg="neutral.900" color="white" _hover={{ bg: "neutral.800" }}
              display="inline-flex" alignItems="center" gap={2} boxShadow="none">
              <Logo size={22} /><SparkIcon />
              <Text fontSize="13.5px" fontWeight="600" display={{ base: "none", md: "block" }}>{tt("a.f9c34d63", "레이에게 묻기")}</Text>
              <Box display={{ base: "none", md: "block" }}><AiTag dark /></Box>
            </Button>
            <Box display={{ base: "none", md: "block" }}><BetaRibbon /></Box>
          </Box>
        </Box>
      )}

      {open && (
        <Flex id="lay-widget-panel" role="dialog" aria-label={tt("a.dd207885", "레이 AI 도우미")} direction="column" position="fixed" zIndex={1200} overflow="hidden"
          right={{ base: 0, md: side ? 0 : "20px" }} bottom={{ base: 0, md: side ? 0 : "76px" }} left={{ base: 0, md: "auto" }} top={{ base: 0, md: side ? 0 : "auto" }}
          w={{ base: "100%", md: "380px" }} h={{ base: "100dvh", md: side ? "100dvh" : "min(600px, calc(100dvh - 96px))" }}
          bg="white" borderWidth={{ base: 0, md: "1px" }} borderColor="neutral.300" borderRadius={{ base: 0, md: side ? 0 : "6px" }} boxShadow={side ? "-8px 0 30px rgba(26,25,24,0.10)" : "0 12px 40px rgba(26,25,24,0.18)"}
          transition={transition}>
          <Flex align="center" gap={2.5} px={3.5} py={3} pr="56px" bg="neutral.900" color="white" flexShrink={0} position="relative" overflow="hidden">
            <BetaRibbon top="8px" right="-28px" />
            <Logo size={22} />
            <Box minW={0}>
              <HStack spacing={1.5} align="center"><Text fontSize="14px" fontWeight="700" lineHeight="1.2">{tt("a.54c22d67", "레이")}</Text><AiTag dark /></HStack>
              <Text fontSize="11.5px" color="neutral.400" mt={0.5}>{tt("a.b2832834", "AI 도우미가 가이드를 근거로 답해요 · 확정 안내는 운영팀 답변이 기준")}</Text>
            </Box>
            <Box flex="1" />
            {messages.length > 0 && <ActionTooltip label={tt("a.558a0cb5", "새 대화")} hasArrow openDelay={300}><IconButton aria-label={tt("a.558a0cb5", "새 대화")} size="sm" variant="ghost" color="neutral.300" _hover={{ bg: "whiteAlpha.200" }} onClick={reset} icon={<Text fontSize="16px" lineHeight="1">↺</Text>} /></ActionTooltip>}
            <ActionTooltip label={tt("a.30e284e8", "레이 설정")} hasArrow openDelay={300}><IconButton aria-label={tt("a.30e284e8", "레이 설정")} size="sm" variant="ghost" color={settings ? "brand.400" : "neutral.300"} _hover={{ bg: "whiteAlpha.200" }} onClick={() => setSettings((s) => !s)} icon={<Text fontSize="15px" lineHeight="1">⚙</Text>} /></ActionTooltip>
            <IconButton aria-label={tt("a.218e2ae6", "닫기")} size="sm" variant="ghost" color="neutral.300" _hover={{ bg: "whiteAlpha.200" }} onClick={close} icon={<Text fontSize="18px" lineHeight="1">×</Text>} />
          </Flex>

          <Box position="relative" flex="1" minH={0} display="flex" flexDirection="column">
            {settings && <SettingsSheet prefs={prefs} onChange={changePrefs} onClose={() => setSettings(false)} onHistory={() => { close(); navigate(scope === "partner" ? PARTNER_HISTORY_PATH : "/settings/lay/history"); }} onHelp={() => { if (scope === "partner") { close(); navigate(`${PARTNER_DOCS_PATH}/p-lay`); } else { window.open(`${GUIDE_URL}/cr-lay`, "_blank", "noopener,noreferrer"); } }} />}
            <Flex align="center" gap={2} px={3.5} py={1.5} fontSize="11px" bg="accent.50" color="accent.800" borderBottom="1px solid" borderColor="accent.100" flexShrink={0}>
              <Text noOfLines={1}>{tt("a.2410c4e3", "지금 보는 화면")} <Text as="span" fontFamily="mono">{location.pathname}</Text></Text>
            </Flex>
            <Box ref={listRef} flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" px={3.5} py={3.5}>
              {messages.length === 0 ? (
                <Box>
                  <Text fontSize="13.5px" color="neutral.800" lineHeight="1.65" borderLeftWidth="2px" borderColor="brand.500" pl={3} mb={4}>
                    {tt("a.7b934e53", "안녕하세요, AI 도우미 레이예요. 지금 보는 화면과 이용 가이드를 근거로 답해요.")}{pickAllowed ? ` ${tt("a.cb3af265", "궁금한 곳을 「화면에서 고르기」로 찍어 물어보셔도 돼요.")}` : ""}
                  </Text>
                  <Text fontSize="11px" fontWeight="600" letterSpacing="0.08em" color="neutral.500" mb={2}>{tt("a.424d209a", "이런 걸 물어보세요")}</Text>
                  <VStack align="stretch" spacing={1.5}>
                    {(SUGGESTIONS[scope] || SUGGESTIONS.creator).map((q) => (
                      <Button key={q} size="sm" h="auto" py={2} px={0} fontWeight="500" fontSize="13px" whiteSpace="normal" textAlign="left" justifyContent="space-between" variant="ghost" color="neutral.700" _hover={{ color: "brand.700", bg: "neutral.50" }} onClick={() => send(q)} rightIcon={<Text as="span" color="neutral.400">→</Text>}>{q}</Button>
                    ))}
                  </VStack>
                </Box>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {messages.map((m, i) => <Message key={i} m={m} scope={scope} onDecide={(d) => decide(i, d)} />)}
                  {sending && <HStack spacing={2} color="neutral.500" fontSize="12.5px"><Spinner size="xs" color="brand.500" /><Text>{tt("a.e5f0a36d", "가이드에서 찾는 중…")}</Text></HStack>}
                  {error && <Text fontSize="12.5px" color="status.error" role="alert">{error}</Text>}
                </VStack>
              )}
            </Box>
            <Box as="form" onSubmit={(e) => { e.preventDefault(); send(input); }} px={3} pt={2} pb={2} bg="neutral.50" borderTop="1px solid" borderColor="neutral.200" flexShrink={0}>
              <input type="text" name="_hp" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
              {(pickAllowed || picked) && (
                <HStack spacing={1.5} mb={2} flexWrap="wrap">
                  {pickAllowed && !isMobile && (
                    <Button size="xs" variant={picking ? "solid" : "outline"} bg={picking ? "brand.50" : "white"} color={picking ? "brand.700" : "neutral.700"} borderColor={picking ? "brand.500" : "neutral.300"} onClick={() => setPicking((p) => !p)}>
                      ⌖ {picking ? tt("a.9122ef6c", "화면에서 요소를 클릭하세요 (Esc 취소)") : tt("a.2db50b1f", "화면에서 고르기")}
                    </Button>
                  )}
                  {picked && (
                    <HStack spacing={1} fontSize="11.5px" color="brand.700" bg="brand.50" border="1px solid" borderColor="rgba(60,183,160,0.4)" borderRadius="6px" px={2} py={0.5} maxW="100%">
                      <Text noOfLines={1}>{tt("a.9309b805", "선택:")} {picked.label}</Text>
                      <Button size="xs" variant="unstyled" minW="auto" h="auto" color="neutral.500" onClick={() => setPicked(null)} aria-label={tt("a.2da8fab5", "선택 해제")}>×</Button>
                    </HStack>
                  )}
                </HStack>
              )}
              <Flex gap={2} align="flex-end">
                <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value.slice(0, LAY_MAX_MESSAGE))} onKeyDown={onKeyDown}
                  placeholder={lockedSeconds > 0 ? tt("a.ea0b89ee", "{{v0}}초 뒤에 다시 질문할 수 있어요", { v0: lockedSeconds }) : tt("a.73d66eb5", "레이에게 물어보기…")}
                  rows={1} resize="none" minH="40px" maxH="120px" fontSize="13.5px" py={2} isDisabled={sending || lockedSeconds > 0} aria-label={tt("a.f765cf57", "질문")}
                  borderRadius="4px" borderColor="neutral.300" bg="white" focusBorderColor="neutral.800" />
                <Button type="submit" h="40px" px={3.5} fontSize="13px" fontWeight="600" bg="neutral.900" color="white" _hover={{ bg: "neutral.800" }} borderRadius="4px" isDisabled={!input.trim() || sending || lockedSeconds > 0}>{tt("a.274cb078", "보내기")}</Button>
              </Flex>
              <Text mt={1.5} fontSize="10.5px" color="neutral.400">{tt("a.2d6311d4", "베타 · 이용 가이드를 근거로 AI 가 답하며 틀릴 수 있어요. 모든 대화는 기록되고 운영팀이 확인할 수 있어요.")}</Text>
            </Box>
          </Box>
        </Flex>
      )}
    </>
  );
}
